/* eslint-disable react/no-danger */
import * as React from 'react';

import { CMLUtils, toPlainText } from '@coursera-web/cml';
import moment from 'moment';

import config from 'js/app/config';

import type { CourseReviewsPageSchemaReview } from 'bundles/reviews/hooks/useCourseReviewsPageData';
import { clearEmptyFields, stringifyJsonLd } from 'bundles/seo/utils/utils';

type CDPReviewsMetaData = {
  aggregrateRating: {
    ratingScore?: string;
    ratingCount?: string;
    reviewCount?: string;
  };
  slug: string;
  courseName: string;
  courseDescription?: string | null;
  partnerName?: string;
  partnerLogoUrl?: string | null;
  reviews: ReadonlyArray<CourseReviewsPageSchemaReview>;
};

type Props = {
  CDPReviewsMetaData: CDPReviewsMetaData;
};

const createCanonicalCourseLink = (slug: string) => `${config.url.base}learn/${slug}`;

const CDPReviewsSchemaMarkup = ({ CDPReviewsMetaData }: Props) => {
  const {
    aggregrateRating: { ratingScore, ratingCount, reviewCount },
    slug,
    courseName,
    courseDescription,
    partnerName,
    partnerLogoUrl,
    reviews,
  } = CDPReviewsMetaData;

  const markupJson = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: courseName,
    brand: partnerName,
    image: partnerLogoUrl,
    description: courseDescription,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratingScore,
      ratingCount,
      reviewCount,
      worstRating: 1,
      bestRating: 5,
    },
    offers: {
      '@type': 'Offer',
      url: createCanonicalCourseLink(slug),
      priceCurrency: 'USD',
      price: 0,
      availability: 'OnlineOnly',
      seller: {
        '@id': 'https://www.coursera.org/#organization',
        '@type': 'Organization',
        name: 'Coursera',
      },
    },
    review: reviews.length
      ? reviews.map((review) => ({
          '@type': 'Review',
          datePublished: moment(review.reviewedAt).format('YYYY-MM-DD'),
          reviewBody: review.reviewText
            ? toPlainText(CMLUtils.create(review.reviewText.cml.value, review.reviewText.cml.dtdId))
            : undefined,

          author: {
            '@type': 'Person',
            name: review.fullName,
          },

          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating,
          },
        }))
      : '',
  };

  const createMarkup = () => ({
    __html: `
        <script type="application/ld+json">
          ${stringifyJsonLd(clearEmptyFields(markupJson))}
        </script>
      `,
  });

  return (
    <div className="rc-CDPReviewsSchemaMarkup">
      <div dangerouslySetInnerHTML={createMarkup()} />
    </div>
  );
};

export default CDPReviewsSchemaMarkup;
